import { useEffect, useState, type JSX } from 'react';
import { getSoundConfig, getSprintConfig, setSoundConfig, setSprintEpoch } from './api';
import { DEFAULT_CHIME_VOLUME, playDoneChime, setChimeVolume } from './sound';
import { DEFAULT_EPOCH_MONDAY_UTC, mondayOfWeekUtc, sprintLabel } from './sprints';

// Admin settings page. Currently: the sprint epoch (start of sprint #1). Reached
// via the gear next to the email; rendered only for admins.
export function SettingsView({ userEmail }: { userEmail: string }): JSX.Element {
  const [epochMs, setEpochMs] = useState(DEFAULT_EPOCH_MONDAY_UTC);
  const [dateStr, setDateStr] = useState(toDateInput(DEFAULT_EPOCH_MONDAY_UTC));
  const [volume, setVolume] = useState(DEFAULT_CHIME_VOLUME);
  const [volBusy, setVolBusy] = useState(false);
  const [volMsg, setVolMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    getSprintConfig()
      .then((c) => {
        const ms = c?.epochMondayMs ?? DEFAULT_EPOCH_MONDAY_UTC;
        setEpochMs(ms);
        setDateStr(toDateInput(ms));
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
    getSoundConfig()
      .then((c) => {
        if (c) setVolume(c.chimeVolume);
      })
      .catch(() => {});
  }, []);

  const onVolumeChange = (v: number): void => {
    setVolume(v);
    setChimeVolume(v); // live preview uses the new value immediately
  };

  const saveVolume = async (): Promise<void> => {
    setVolBusy(true);
    setVolMsg('');
    setError('');
    try {
      await setSoundConfig(volume, userEmail);
      setChimeVolume(volume);
      setVolMsg('Saved.');
    } catch (e) {
      setError(String(e));
    } finally {
      setVolBusy(false);
    }
  };

  const save = async (): Promise<void> => {
    setBusy(true);
    setMsg('');
    setError('');
    try {
      const picked = Date.parse(`${dateStr}T00:00:00Z`);
      if (Number.isNaN(picked)) throw new Error('Pick a valid date');
      const monday = mondayOfWeekUtc(picked);
      await setSprintEpoch(monday, userEmail);
      setEpochMs(monday);
      setDateStr(toDateInput(monday));
      setMsg(`Saved. Sprint ${sprintLabel(1, monday)}`);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="placeholder">Loading settings...</div>;

  return (
    <div className="settings">
      <h2>
        <i className="fas fa-cog" /> Admin settings
      </h2>

      <section className="setting-block">
        <h3>Sprint epoch</h3>
        <p className="muted">
          Start of sprint #1. Any day works - it snaps to that week&apos;s Monday. Changing it
          renumbers all sprints in the Tickets tab.
        </p>
        <label className="setting-row">
          <span>Sprint #1 starts</span>
          <input type="date" value={dateStr} onChange={(e) => setDateStr(e.target.value)} />
        </label>
        <p className="muted">Current: {sprintLabel(1, epochMs)}</p>
        <button className="primary" disabled={busy} onClick={save}>
          {busy ? 'Saving...' : 'Save'}
        </button>
        {msg && <div className="ok">{msg}</div>}
        {error && <div className="error">{error}</div>}
      </section>

      <section className="setting-block">
        <h3>Ticket done volume</h3>
        <p className="muted">
          Loudness of the reward sound played when a ticket is moved to Done (0 = muted).
        </p>
        <label className="setting-row">
          <span>Volume</span>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={volume}
            onChange={(e) => onVolumeChange(Number(e.target.value))}
          />
          <span className="muted">{Math.round(volume * 100)}%</span>
          <button onClick={playDoneChime} title="Test sound">
            <i className="fas fa-play" /> Test
          </button>
        </label>
        <button className="primary" disabled={volBusy} onClick={saveVolume}>
          {volBusy ? 'Saving...' : 'Save'}
        </button>
        {volMsg && <div className="ok">{volMsg}</div>}
      </section>
    </div>
  );
}

// ms (UTC midnight) -> yyyy-mm-dd for a <input type="date">.
function toDateInput(ms: number): string {
  const d = new Date(ms);
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
